import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { Box, Grid, Stack, Typography } from '@mui/material'
import { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { GalleryFilter, useClientGallery } from '../_main/contexts'
import { VisualFileType, VisualStatus } from 'constants/visual'
import { useGalleryAdditionalVisuals, useGalleryAssignment, useGalleryConstants, useGalleryDeal, useGalleryOrder, useGalleryProduct, useGalleryVisualSelection, useGalleryVisualType, useGalleryVisuals, useGalleryVisualsDownloadArchive, useGalleryVisualsMeta } from '../../contexts'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { APIRequestErrorType } from 'constants/API'
import { ClientGalleryBetaSelfEditingCard } from '../ClientGalleryPhoto/ClientGalleryBetaSelfEditingCard.module'
import { ClientGalleryLowQualityDisclaimerCard } from '../ClientGalleryPhoto/ClientGalleryLowQualityDisclaimerCard.module'
import { ClientGalleryPhotoSectionActionFilterPanels } from './ClientGalleryPhotoSectionActionFilterPanels'
import { ClientGalleryPhotoSectionFilteredFavourites } from './ClientGalleryPhotoSectionFilteredFavourites.module'
import { ClientGalleryPhotoSectionFilteredUnpurchased } from './ClientGalleryPhotoSectionFilteredUnpurchased'
import { ClientGalleryPhotoSectionNotPurchasedAny } from './ClientGalleryPhotoSectionNotPurchasedAny.module'
import { ClientGalleryPhotoStagingCard } from '../ClientGalleryPhoto/ClientGalleryPhotoStagingCard.module'
import { Color } from 'constants/assets'
import { DownloadDropdownItem } from 'components/common/Gallery/GalleryImage/DownloadDropdownIcon'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { GalleryImage } from 'components/common/Gallery/GalleryImage'
import { GallerySection } from 'components/common/Gallery/GallerySection'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import { MUIBadge } from 'components/common/MUIBadge'
import { MOBILE_VIEW_WIDTH_QUERY } from 'constants/styling/theme'
import { Path } from 'constants/router'
import ReactLoading from 'react-loading'
import { VisualClientDTO } from 'models/visuals'
import { getPropertyPathname } from 'utils/helpers/getPropertyPathname'
import { useImageRegex } from 'utils/hooks'
import { withVisualSelected } from '../../blurPairs'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Trans, useTranslation } from 'react-i18next'

enum ListingType {
  PURCHASED = 'PURCHASED',
  UNPURCHASED = 'UNPURCHASED',
  FAVOURITES_PURCHASED = 'FAVOURITES_PURCHASED',
  FAVOURITES_UNPURCHASED = 'FAVOURITES_UNPURCHASED',
  AI_ENHANCED = 'AI_ENHANCED',
}

enum GridColumnSize {
  DEFAULT = 'DEFAULT',
  SMALL = 'SMALL',
}

/**
 * @interface Props
 */
interface Props {
  /** The list of favorite visuals */
  favorited: Set<string>
  /** setState action to open expanded visual popup */
  setIsExpandedVisualOpen: Dispatch<SetStateAction<boolean>>
  /** setState action to set expanded visual name */
  setExpandedVisualName: Dispatch<SetStateAction<string | undefined>>
  /** setState action to toggle favorite on image */
  toggleFavoriteOnImage: (filename?: string) => void
  /** OnClick action to open Photo Studio */
  onOpenPhotoStudio: (file: File, isSelfEditedVisual: boolean) => void
  /** Whether the favorite button has disabled clicking */
  isFavoriteDisabled: boolean,
  /** Metadata of an assignment visuals */
  visualsMetadata: Record<string, VisualClientDTO>
}

/**
 * Client gallery photo section.
 * 
 * @example <ClientGalleryPhotoSection />
 */
export const ClientGalleryPhotoSection: FC<Props> = ({
  favorited,
  setIsExpandedVisualOpen,
  setExpandedVisualName,
  toggleFavoriteOnImage,
  isFavoriteDisabled = false,
  visualsMetadata,
  onOpenPhotoStudio
}) => {
  const { pathname } = useLocation()
  const { id } = useParams<{ id: string }>()

  const isBetaSelfEditingCard = useFeatureFlag(FeatureFlag.BETA_SELF_EDIT_WIDGET)

  const { t } = useTranslation(['gallery'])

  const isPropertyPathname = useMemo(() => getPropertyPathname(pathname), [pathname])

  const isMobileView = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)
  const navigate = useNavigate()

  const { initiateArchiveDownload } = useGalleryVisualsDownloadArchive()

  const [isBetaSelfEditingCardClosed, setIsBetaSelfEditingCardClosed] = useState(false)

  // DATA QUERIES

  const { stagingAssignments } = useGalleryDeal()
  const { sortEntriesFunction } = useGalleryOrder()

  const {
    purchasedVisualsKeys,
    purchasedVisualsExist,
    unPurchasedVisualsExist,
    unPurchasedVisualsKeys,
    prepurchasedVisualsRemaining,
    blurPartners,
  } = useGalleryVisualsMeta()

  const { isAdditionalVisualPurchaseDisabled } = useGalleryAdditionalVisuals()

  const {
    downloadVisualsEntries,
    listedVisuals,
  } = useGalleryVisuals()

  const {
    thumbnailType,
    webType,
  } = useGalleryVisualType()

  const {
    selected,
    setSelected,
    selectedNotPurchasedVisualsKeys,
    selectedNotPurchasedVisualsCount,
    toggleSelectOnImage,
  } = useGalleryVisualSelection()

  const {
    assignmentId,
    filter,
    isToolBarOpen,
    setIsToolBarOpen,
    enableStagingFlowGallery
  } = useClientGallery()

  const {
    isVideo,
    isFloorPlan,
    isAIEditingProduct,
    isSelfUploadProduct
  } = useGalleryProduct()

  const {
    product,
    assignmentData,
    logGalleryEvent,
  } = useGalleryAssignment()

  const { stagesUnlockingStagingFlow } = useGalleryConstants()

  const imageNameReplacePattern = useImageRegex(assignmentId)

  const onCloseBetaCard = () => {
    setIsBetaSelfEditingCardClosed(true)
    saveStateToLocalStorage(true)
    logAnalyticsEvent(AnalyticsEvent.PINTURA_CLOSE_UPSELL_ON_GALLERY, { assignmentId })
  }

  // Save state to local storage
  const saveStateToLocalStorage = useCallback((isBetaSelfEditingCardClosed: boolean) => {
    localStorage.setItem(
      'clientGalleryBetaSelfEditingCardClosed', JSON.stringify(isBetaSelfEditingCardClosed)
    )
  }, [])

  // Load state from local storage
  const loadStateFromLocalStorage = useCallback(() => {
    const state = localStorage.getItem('clientGalleryBetaSelfEditingCardClosed')
    if (state) {
      setIsBetaSelfEditingCardClosed(Boolean(state))
    }
  }, [])

  const handleDownloadImageClick = useCallback((itemKey: DownloadDropdownItem, fileName: string) => {
    if (itemKey === DownloadDropdownItem.DIRECT_DOWNLOAD) {
      initiateArchiveDownload(new Set<string>([fileName]))
    }

    if (itemKey === DownloadDropdownItem.EXPORTING_OPTIONS) {
      // Add the image (and the other version of its photo) to the selection to open the visuals editing page
      const nextSelected = withVisualSelected(selected, fileName, blurPartners)
      setSelected(nextSelected)
      setIsToolBarOpen(true)

      if (isPropertyPathname) {
        if (id && assignmentId) {
          navigate(Path.PROPERTY_ASSIGNMENT_EXPORTING_OPTIONS
            .replace(':id', id.toString())
            .replace(':assignmentId', assignmentId.toString()),
            { state: { selectedVisuals: nextSelected } })
        }
      } else {
        navigate(Path.GALLERY_VISUALS_EDIT.replace(':id', assignmentId.toString()))
      }
    }
  }, [assignmentId, id, initiateArchiveDownload, isPropertyPathname, navigate, selected, setSelected, setIsToolBarOpen, blurPartners])

  // Load state on component mount
  useEffect(() => {
    loadStateFromLocalStorage()
  }, [loadStateFromLocalStorage])

  const onOpenExpandedVisualPopup = useCallback((name?: string) => {
    if (!name) return
    setIsExpandedVisualOpen(true)
    setExpandedVisualName(name)
    logGalleryEvent(AnalyticsEvent.GALLERY_PHOTO_FULL_PREVIEW, {
      productKind: product?.kind
    })
  }, [logGalleryEvent, product?.kind, setExpandedVisualName, setIsExpandedVisualOpen])

  const onFloorPlannerLinkClick = useCallback((e: React.MouseEvent<Element, MouseEvent>) => {
    e.stopPropagation()

    logAnalyticsEvent(AnalyticsEvent.FLOOR_PLANNER_LINK_CLICKED, {
      galleryType: 'CLIENT',
      assignmentId: assignmentData?.id,
      productCategory: product?.category,
      productKind: product?.kind,
    })
  }, [assignmentData?.id, product?.category, product?.kind])


  const preloadListing = useCallback(() => {
    const tupleArray: [string, string, JSX.Element][] = listedVisuals.map(visual => {
      const reactKey = visual.name
      return [visual.name, reactKey, (
        <GalleryImage key={reactKey}>
          <div className="text">
            <ReactLoading color={Color.GRAY_TEXT} type="cylon" className="spinner" />
          </div>
        </GalleryImage>
      )]
    })

    return tupleArray
  }, [listedVisuals])

  const downloadListing = useCallback(() => {
    const tupleArray: [string, string, JSX.Element][] = downloadVisualsEntries.map(([key, allTypes], index) => {
      const isPurchasedVisuals = purchasedVisualsKeys.has(key)
      let visualThumbType = isPurchasedVisuals ? VisualFileType.POST_THUMB : thumbnailType
      const visualWebType = isPurchasedVisuals ? VisualFileType.POST_WEB : webType
      let img = allTypes?.[visualThumbType]
      if (!img && thumbnailType === VisualFileType.POST_THUMB_WATERMARK) {
        visualThumbType = VisualFileType.POST_THUMB
        img = allTypes?.[visualThumbType]
      } else if (!img && thumbnailType === VisualFileType.POST_THUMB) {
        visualThumbType = VisualFileType.POST_THUMB_WATERMARK
        img = allTypes?.[visualThumbType]
      }

      if (!img) {
        // Render a fallback UI if image hasn't matched any type and no error returned. This is to avoid blank space during loading state.
        const reactKey = key
        return [key, reactKey, (
          <GalleryImage key={reactKey}>
            <div className="text">
              <ReactLoading color={Color.GRAY_TEXT} type="cylon" className="spinner" />
            </div>
          </GalleryImage>
        )]
      }

      const isError = img.request.error_type !== APIRequestErrorType.NONE
      const filename = img?.file?.name ?? ''
      const label = filename.replace(imageNameReplacePattern, '')
      const reactKey = key
      const visualMetadata = visualsMetadata[img?.file?.name ?? '']
      const visualStatus = visualsMetadata[img?.file?.name ?? '']?.status
      const originalFilename = visualMetadata?.originalName || label
      const isDownloadable = !isError && purchasedVisualsKeys.has(visualMetadata?.name) && visualStatus !== VisualStatus.PROCESSING

      return [key, reactKey, (
        <GalleryImage
          key={reactKey}
          label={label}
          filename={filename}
          originalFilename={originalFilename}
          imageUrl={img.signedUrl}
          errorMessage={<Trans t={t} i18nKey="gallery:error_not_found_and_retry" />}
          isError={isError}
          isSelectable={!isError}
          isSelected={selected.has(img?.file?.name ?? '')}
          isHoverSelectable={!!purchasedVisualsExist && !selected.size}
          isFavoriteSelectable={!isError}
          isSelectionDisabled={isAdditionalVisualPurchaseDisabled && unPurchasedVisualsKeys.has(img?.file?.name ?? '') && !selectedNotPurchasedVisualsKeys.has(img?.file?.name ?? '') && selectedNotPurchasedVisualsCount >= prepurchasedVisualsRemaining}
          isFavorite={favorited.has(img?.file?.name ?? '')}
          isFavoriteHoverActive={(!isMobileView || !isToolBarOpen)}
          isFavoriteDisabled={isFavoriteDisabled}
          isSelfUpload={isSelfUploadProduct}
          isRenameAvailable={isDownloadable}
          visualMetadata={visualMetadata ?? null}
          isEditable={!visualMetadata?.floorPlanSelfEditUrl && visualMetadata?.name && !isError ? purchasedVisualsKeys.has(visualMetadata.name) : false}
          isDownloadable={isDownloadable}
          status={visualStatus}
          onFloorPlannerLinkClick={onFloorPlannerLinkClick}
          onOpenPhotoStudio={() => onOpenPhotoStudio(img?.file ? img?.file : new File([], filename), visualsMetadata[img?.file?.name ?? ''].selfEdited)}
          onSelect={() => {
            toggleSelectOnImage(img?.file?.name)
            setIsToolBarOpen(true)
          }}
          onFavSelect={() => {
            toggleFavoriteOnImage(img?.file?.name)
          }}
          onClick={() => {
            if (isMobileView && isToolBarOpen) return toggleSelectOnImage(img?.file?.name)
            if (!(visualWebType && allTypes?.[visualWebType]?.signedUrl)) return
            onOpenExpandedVisualPopup(img?.file?.name)
          }}
          onDownloadOptionClick={(itemKey) => handleDownloadImageClick(itemKey, img?.file?.name ?? '')}
          labelTopSlot={blurPartners.has(filename) && (
            <MUIBadge
              size="sm"
              color="blue"
              icon={<LinkRoundedIcon fontSize="small" />}
              label={t('gallery:blur_pair_badge')}
              title={t('gallery:blur_pair_badge_tooltip')}
            />
          )}
        />
      )]
    })

    return tupleArray
  }, [downloadVisualsEntries, thumbnailType, imageNameReplacePattern, visualsMetadata, t, selected, purchasedVisualsExist, isToolBarOpen, isAdditionalVisualPurchaseDisabled, unPurchasedVisualsKeys, selectedNotPurchasedVisualsKeys, selectedNotPurchasedVisualsCount, prepurchasedVisualsRemaining, favorited, isMobileView, isFavoriteDisabled, isSelfUploadProduct, purchasedVisualsKeys, onFloorPlannerLinkClick, onOpenPhotoStudio, toggleSelectOnImage, setIsToolBarOpen, toggleFavoriteOnImage, webType, onOpenExpandedVisualPopup, handleDownloadImageClick, blurPartners])

  const createVisualListing = (filteredEntries: [string, [string, JSX.Element]][], type: ListingType) => {
    const entriesCount = filteredEntries.length
    const isTwoColumnViewProduct = isVideo || (isFloorPlan && entriesCount > 1)
    const isOneColumnViewProduct = isFloorPlan && entriesCount === 1

    const getGridColumns = (size: GridColumnSize) => {
      if (isOneColumnViewProduct) return 12
      if (isTwoColumnViewProduct) return 6

      return size === GridColumnSize.DEFAULT ? 3 : 4
    }

    const addStagingTodoInfoCardIfNeeded = () => {
      const assignmentsToSet = stagingAssignments.filter((assignment) => stagesUnlockingStagingFlow.has(assignment.stage))
      if (!enableStagingFlowGallery || type !== ListingType.PURCHASED || !assignmentsToSet.length) return

      if (isBetaSelfEditingCard && !isBetaSelfEditingCardClosed) {
        if (filteredEntries.length + 1 <= 3) {
          filteredEntries.push(['stagingTodoInfoCard', ['stagingTodoInfoCard', <ClientGalleryPhotoStagingCard key='staging-card' />]])
        } else {
          filteredEntries.splice(6, 0, ['stagingTodoInfoCard', ['stagingTodoInfoCard', <ClientGalleryPhotoStagingCard key='staging-card' />]])
        }
      } else {
        if (filteredEntries.length + 1 <= 3) {
          filteredEntries.push(['stagingTodoInfoCard', ['stagingTodoInfoCard', <ClientGalleryPhotoStagingCard key='staging-card' />]])
        } else {
          filteredEntries.splice(3, 0, ['stagingTodoInfoCard', ['stagingTodoInfoCard', <ClientGalleryPhotoStagingCard key='staging-card' />]])
        }
      }
    }

    addStagingTodoInfoCardIfNeeded()

    const addBetaSelfEditingCard = () => {
      // Show beta self editing card only if the user has an active subscription,
      // the feature flag is enabled, the card is not closed and there is at least one visual that can be self-edited (for Floor plans orders it means there is unless one floor plan without floorplanner link)
      if (!isBetaSelfEditingCard || isBetaSelfEditingCardClosed || type !== ListingType.PURCHASED || !Object.values(visualsMetadata).some(visualMetadata => !visualMetadata?.floorPlanSelfEditUrl)) return

      if (filteredEntries.length + 1 <= 3) {
        filteredEntries.push(['betaSelfEditingCard', ['betaSelfEditingCard', <ClientGalleryBetaSelfEditingCard key='betaSelfEditingCard' onClose={onCloseBetaCard} />]])
      } else {
        filteredEntries.splice(3, 0, ['betaSelfEditingCard', ['betaSelfEditingCard', <ClientGalleryBetaSelfEditingCard key='betaSelfEditingCard' onClose={onCloseBetaCard} />]])
      }
    }

    addBetaSelfEditingCard()

    const addLowQualityDisclaimerCard = () => {
      if (!unPurchasedVisualsExist) return
      if (type !== ListingType.UNPURCHASED || !isAIEditingProduct || isSelfUploadProduct) return

      if (filteredEntries.length > 0) {
        filteredEntries.unshift(['lowQualityDisclaimerCard', ['lowQualityDisclaimerCard', <ClientGalleryLowQualityDisclaimerCard key='lowQualityDisclaimerCard' />]])
      }
    }

    addLowQualityDisclaimerCard()

    return (
      <Grid container spacing={2}>
        {filteredEntries.map(([key, [reactKey, node]]) => (
          <Grid key={reactKey} item xs={isOneColumnViewProduct ? 12 : 6} sm={getGridColumns(GridColumnSize.SMALL)} md={getGridColumns(GridColumnSize.DEFAULT)} lg={getGridColumns(GridColumnSize.DEFAULT)}>
            {node}
          </Grid>
        ))}
      </Grid>
    )
  }

  const visualsListing = (type: ListingType) => {
    const map: Map<string, [string, JSX.Element]> = new Map()
    let sortedEntries: [string, [string, JSX.Element]][]

    for (let [key, reactKey, node] of preloadListing()) map.set(key, [reactKey, node])
    for (let [key, reactKey, node] of downloadListing()) map.set(key, [reactKey, node])

    const entries: [string, [string, JSX.Element]][] = Array.from(map.entries())

    switch (type) {
      case ListingType.FAVOURITES_PURCHASED:
        sortedEntries = entries.filter(([visualName, visualProps]) => purchasedVisualsKeys.has(visualName) && visualProps[1].props.isFavorite).sort(sortEntriesFunction)
        break

      case ListingType.FAVOURITES_UNPURCHASED:
        sortedEntries = entries.filter(([visualName, visualProps]) => !purchasedVisualsKeys.has(visualName) && visualProps[1].props.isFavorite).sort(sortEntriesFunction)
        break

      case ListingType.PURCHASED:
        sortedEntries = entries.filter(([visualName]) => purchasedVisualsKeys.has(visualName)).sort(sortEntriesFunction)
        break

      case ListingType.UNPURCHASED:
        sortedEntries = entries.filter(([visualName]) => !purchasedVisualsKeys.has(visualName)).sort(sortEntriesFunction)
        break

      case ListingType.AI_ENHANCED:
        sortedEntries = entries.filter(([visualName]) => visualsMetadata[visualName]?.isAiEnhanced === true).sort(sortEntriesFunction)
        break

      default:
        sortedEntries = []
        break
    }

    return createVisualListing(sortedEntries, type)
  }

  const purchasedVisuals = visualsListing(ListingType.PURCHASED)
  const unPurchasedVisuals = visualsListing(ListingType.UNPURCHASED)
  const favouritesPurchasedVisuals = visualsListing(ListingType.FAVOURITES_PURCHASED)
  const favouritesUnPurchasedVisuals = visualsListing(ListingType.FAVOURITES_UNPURCHASED)
  const aiEnhancedVisuals = visualsListing(ListingType.AI_ENHANCED)

  const aiEnhancedVisualsCount = useMemo(() => {
    return Object.values(visualsMetadata).filter(visual => visual?.isAiEnhanced === true).length
  }, [visualsMetadata])

  const showUnpurchasedSection = purchasedVisualsExist && unPurchasedVisualsExist && filter === GalleryFilter.ALL
  const showPurchasedSection = purchasedVisualsExist && filter !== GalleryFilter.FAVORITES && filter !== GalleryFilter.AI_ENHANCED
  const showFavouritesSection = filter === GalleryFilter.FAVORITES
  const showAiEnhancedSection = filter === GalleryFilter.AI_ENHANCED

  return (
    <Stack marginTop={{ xs: '2.4rem', md: '3.2rem' }}>

      {/** FLOW A - NOT PURCHASED ANY VISUALS YET - SHOWS INFO MESSAGE ABOUT REQUIRED SELECTION & FILTERS ALL + FAVOURITES */}
      {!purchasedVisualsExist &&
        <ClientGalleryPhotoSectionNotPurchasedAny unPurchasedVisuals={unPurchasedVisuals} />
      }

      {/** FLOW B - FILTER ALL - PURCHASED VISUALS - SHOWN THE FILTERS & ACTIONS PANEL WHEN VISUALS WERE SELECTED ALREADY */}
      {purchasedVisualsExist &&
        <ClientGalleryPhotoSectionActionFilterPanels />
      }

      {/** FLOW B - FILTERES PURCHASED ONLY */}
      {showPurchasedSection &&
        <GallerySection>{purchasedVisuals}</GallerySection>
      }

      {/** FLOW B - FILTERES ALL - ADDS UNPURCHASED VISUALS SECTION */}
      {showUnpurchasedSection &&
        <ClientGalleryPhotoSectionFilteredUnpurchased unPurchasedVisuals={unPurchasedVisuals} />
      }

      {/** FLOW B - FILTERES FAVOURITES ONLY */}
      {showFavouritesSection &&
        <ClientGalleryPhotoSectionFilteredFavourites favouritesPurchasedVisuals={favouritesPurchasedVisuals} favouritesUnPurchasedVisuals={favouritesUnPurchasedVisuals} />
      }

      {/** FLOW B - FILTERES AI ENHANCED ONLY */}
      {showAiEnhancedSection &&
        (aiEnhancedVisualsCount > 0 ? (
          <GallerySection>{aiEnhancedVisuals}</GallerySection>
        ) : (
          <Box marginTop="4rem">
            <Typography variant="text-md">{t('filters.no_ai_enhanced')}</Typography>
          </Box>
        ))
      }

    </Stack>
  )
}
